import { FC, ReactElement } from 'react';
import {
  FAccordionRow,
  FImage,
  FInfoCallout,
  FLink,
  FLinkListItem,
  FMoleculeCallout,
  FTable,
  FVideo
} from '@belong/contentful';
import { ALERT_POSITION, Entry, IAccordionRow, IRichTextProps } from '@belong/types';
import { AccordionRow } from '../../../../actions/Accordion/AccordionRow';
import { Button } from '../../../../actions/Button';
import CountryList from '../../../../contentBlocks/CountryList/CountryList';
import { InfoCallout } from '../../../../contentBlocks/InfoCallout';
import MediaCaptionBlock from '../../../../contentBlocks/MediaCaptionBlock';
import { Alert, CountdownTimer } from '../../../../feedback';
import { Callout } from '../../../../feedback/Callout/Callout';
import { Icon } from '../../../../Icon';
import { AccordionList, LinkList } from '../../../../lists';
import { Column, Columns } from '../../../../ui';
import { Footnotes, TableV2, TBody, THead } from '../../../TableV2';
import { Copy } from '../../Copy';
import { Heading } from '../../Heading';

/**
 * The recursive rich text renderer, injected by the caller to avoid a circular
 * import between this module and RichText.tsx (which builds its NODES map from
 * the renderers exported here).
 */
type RichTextRenderer = FC<Omit<IRichTextProps, 'dictionary'>>;

export const elementLink = (entry: Entry<any>): ReactElement => <Button {...FLink(entry)} />;

export const elementSystemIcon = (entry: Entry<any>): ReactElement => {
  const { fields } = entry;
  return <Icon name={fields.icon} size={fields.size?.toLowerCase()} role="presentation" className="inline-icon" />;
};

export const elementTable = (entry: Entry<any>, RichTextBase: RichTextRenderer): ReactElement => {
  const { bodyRows, caption, enableHeaderColumn, footnotes, footnoteId, headerRow } = FTable(entry);
  if (!bodyRows) {
    return <></>;
  }

  /* eslint-disable react/no-array-index-key */
  return (
    <div className="table">
      <TableV2 caption={caption} id={footnoteId}>
        {headerRow && (
          <THead>
            <tr>
              {headerRow.map((cell, cellIdx) =>
                enableHeaderColumn && !cellIdx ? (
                  <td key={cellIdx} />
                ) : (
                  <th key={cellIdx}>
                    <RichTextBase html={cell} />
                  </th>
                )
              )}
            </tr>
          </THead>
        )}
        <TBody>
          {bodyRows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {row.map((cell, cellIdx) =>
                enableHeaderColumn && !cellIdx ? (
                  <th key={cellIdx}>
                    <RichTextBase html={cell} />
                  </th>
                ) : (
                  <td key={cellIdx}>
                    <RichTextBase html={cell} />
                  </td>
                )
              )}
            </tr>
          ))}
        </TBody>
      </TableV2>
      {footnoteId && footnotes?.length && <Footnotes footnotes={footnotes} footnoteId={footnoteId} />}
    </div>
  );

  /* eslint-enable react/no-array-index-key */
};

export const moleculeCallout = (entry: Entry<any>): ReactElement => <Callout {...FMoleculeCallout(entry)} fullwidth />;

export const moleculeAlert = (entry: Entry<any>, RichTextBase: RichTextRenderer): ReactElement => (
  <Alert position={ALERT_POSITION.BLOCK} type={entry.fields.level}>
    <RichTextBase html={entry.fields.message} />
  </Alert>
);

export const moleculeCountdownTimer = (entry: Entry<any>): ReactElement => {
  const { countdownTitle, countdownEnd } = entry.fields;
  return <CountdownTimer countdownTitle={countdownTitle} countdownEnd={countdownEnd} />;
};

export const moleculeLinkList = (entry: Entry<any>): ReactElement => {
  const items = entry.fields.items.map(FLinkListItem);
  return (
    <Columns>
      <Column width={{ md: '5/6', lg: '2/3', xl: '1/2' }}>
        <LinkList items={items} />
      </Column>
    </Columns>
  );
};

export const countryList = (entry: Entry<any>): ReactElement => {
  const hasColumns = { xs: 2, sm: 3, md: 5 };
  const { countries } = entry.fields;
  return <CountryList countries={countries} hasColumns={hasColumns} />;
};

export const moleculeMediaCaptionBlock = (entry: Entry<any>): ReactElement => {
  const { image, imageAspectRatio, video, ...fields } = entry.fields;
  const props = {
    ...fields,
    image: image && FImage(image),
    aspectRatio: imageAspectRatio,
    video: video && FVideo(video)
  };
  return <MediaCaptionBlock {...props} />;
};

export const moleculeInformationCalloutRow = (entry: Entry<any>): ReactElement => {
  const callout = FInfoCallout(entry);
  return <InfoCallout {...callout} />;
};

export const sectionAccordionList = (entry: Entry<any>, RichTextBase: RichTextRenderer): ReactElement => {
  const { accordionRows, description, heading } = entry.fields;

  const rows: IAccordionRow[] = accordionRows.map(FAccordionRow);

  return (
    <>
      {heading && <Heading as="h2">{heading}</Heading>}
      {description && <Copy>{description}</Copy>}
      <AccordionList
        items={rows.map(({ body, ...accordion }) => {
          return (
            <AccordionRow key={accordion.identifier} {...accordion}>
              {body && <RichTextBase {...body} />}
            </AccordionRow>
          );
        })}
      />
    </>
  );
};
